import { memo, useEffect, useState } from 'react'
import { PageId, PricePresentation, PricePresentationProps, PriceProperties } from '@vp/price-platform'
import equal from 'fast-deep-equal/es6/index'
import { Skeleton } from 'client/components/common/Skeleton'
import { PricePresentationWrapperPropTypes } from './interface'
import { getPricePresentationConfig } from './utils'

export const PricePresentationWrapper = memo(({ presentationType, pageId, formattedPrices, isLoading }: PricePresentationWrapperPropTypes) => {
  const [priceConfig, setPriceConfig] = useState<PricePresentationProps | null>(null)
  const {
    minimumFractionDigits = 0,
    locale = '',
    currency = '',
    vatInc = false,
    quantity = 1,
    totalDiscountedPrice = { taxed: 0, untaxed: 0 },
    totalListPrice = { taxed: 0, untaxed: 0 },
    unitDiscountedPrice = { taxed: 0, untaxed: 0 },
    unitListPrice = { taxed: 0, untaxed: 0 },
  } = formattedPrices ?? {}

  const priceProperties: PriceProperties = {
    minimumFractionDigits,
    locale,
    currency,
    vatInc,
    quantity,
  }

  useEffect(() => {
    if (formattedPrices) {
      const config = getPricePresentationConfig(pageId, priceProperties, presentationType, {
        totalDiscountedPrice,
        totalListPrice,
        unitDiscountedPrice,
        unitListPrice,
      })

      setPriceConfig(config)
    }
  }, [formattedPrices])

  /* For loading states, we have different handling for tiles and quick view. For tiles,
     we show it on every data fetch i.e changing filters etc.
     but for quick view only on initial load as it's very fast and showing a shimmer in b/w gives flickering feel. */
  const shouldShowInitialLoadingState = isLoading && !priceConfig && pageId === PageId.GalleryQuickView
  const shouldShowNextLoadingState = isLoading && pageId === PageId.Gallery
  const shouldShowPricing = priceConfig && !shouldShowNextLoadingState && !shouldShowInitialLoadingState

  return (
    <>
      {shouldShowInitialLoadingState && <Skeleton />}
      {shouldShowNextLoadingState && <Skeleton />}
      {shouldShowPricing && <PricePresentation {...priceConfig} />}
    </>
  )
}, equal)
PricePresentationWrapper.displayName = 'PricePresentationWrapper'
